import { Card, Group, Stack, Text } from '@mantine/core';
import { ReactNode } from 'react';
import { IconChevronRight } from '@tabler/icons-react';
import { Link } from 'react-router';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { ScenarioScope } from '../scope';
import { ScenarioSummary } from '../types';
import { LongevityMeter } from './LongevityMeter';
import classes from './ModelList.module.css';
import {
  comparedWithPrimary,
  legacyFigure,
  legacyLabel,
  lifeExpectancyLabel,
  modelOutcome,
} from './modelOutcome';
import { ModelMeta, modelTarget, PendingSyncNotice, PrimaryMark } from './modelParts';
import { OutcomeVerdict } from './OutcomeVerdict';
import { modelTimeline } from './timeline';

type Props = {
  scenario: ScenarioSummary;
  // The primary model of the group the row sits in, which other models are measured against.
  primary: ScenarioSummary | null;
  // The years every bar in the group spans, so their plans read against each other.
  scale: number;
  scope: ScenarioScope;
  menu: ReactNode;
};

export function ModelRow({ scenario, primary, scale, scope, menu }: Props) {
  const currency = useCurrency();
  const kpis = scenario.kpis;
  const outcome = modelOutcome(kpis);
  const timeline = modelTimeline(kpis);
  const isPrimary = scenario === primary;
  const comparison =
    primary && !isPrimary ? comparedWithPrimary(kpis, primary.kpis, currency) : null;

  return (
    <Card
      withBorder
      radius="md"
      p="md"
      className={classes.row}
      data-primary={isPrimary || undefined}
      data-testid="cashflow-model-card"
    >
      <div className={classes.columns}>
        <Stack gap={2} className={classes.name}>
          {isPrimary && <PrimaryMark label="Primary model" />}
          {/* The name is the link into the model; its hit area stretches over the whole row. */}
          <Link to={modelTarget(scenario, scope)} className={classes.modelLink}>
            <Text size="sm" fw={600} lineClamp={2}>
              {scenario.label}
            </Text>
          </Link>
          <ModelMeta scenario={scenario} />
        </Stack>
        <Stack gap="xxs" className={classes.outcome}>
          <OutcomeVerdict outcome={outcome} />
          {timeline && outcome.status !== 'notProjected' && (
            <LongevityMeter
              status={outcome.status}
              timeline={timeline}
              scale={Math.max(scale, timeline.length)}
              lifeExpectancyLabel={lifeExpectancyLabel(timeline)}
            />
          )}
        </Stack>
        <Stack gap={0} align="flex-end" className={classes.figures}>
          <Text size="md" fw={600}>
            {legacyFigure(scenario, currency)}
          </Text>
          {kpis && (
            <Text size="xs" c="dimmed" ta="right">
              {legacyLabel(kpis)}
            </Text>
          )}
          {comparison && (
            <Text size="xs" c="dimmed" ta="right">
              {comparison}
            </Text>
          )}
        </Stack>
        <Group gap="xxs" wrap="nowrap" className={classes.chevron}>
          {menu}
          <IconChevronRight size={16} color="var(--mantine-color-gray-4)" aria-hidden />
        </Group>
      </div>
      {scenario.hasPendingSync && <PendingSyncNotice />}
    </Card>
  );
}
